import { useCallback, useEffect, useRef, useState } from "react";
import { PencilLine } from "lucide-react";
import { api, tokens } from "../lib/api";
import Header from "./Header";
import SectionNav from "./SectionNav";
import ReportForm from "./ReportForm";
import StatusSection from "./StatusSection";
import BlockerSection from "./BlockerSection";
import MyReports from "./MyReports";
import TeamReports from "./TeamReports";
import DisciplineGrid from "./DisciplineGrid";
import Trends from "./Trends";
import Ranking from "./Ranking";
import Panel from "./Panel";
import WelcomeCard from "./WelcomeCard";
import InstallPrompt from "./InstallPrompt";

function sectionOrder(data) {
  const me = data.me;
  if (!me) return ["status", "blockers", "team", "discipline", "trends", "ranking", "panel"];
  const form = me.mustReport ? ["form"] : [];
  const mine = me.mustReport ? ["mine"] : [];
  if (me.privileged) return ["status", "blockers", ...form, "team", ...mine, "discipline", "trends", "ranking", "panel"];
  return ["welcome", ...form, "blockers", "status", ...mine, "team", "discipline", "trends", "ranking"];
}

function useFormVisible(enabled, formKey) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const el = document.getElementById("form");
    if (!el) return undefined;
    const obs = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.05 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [enabled, formKey]);
  return visible;
}

export default function Dashboard({ onLogout, onLogin }) {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(null);
  const [lastRefresh, setLastRefresh] = useState("");
  const [editReport, setEditReport] = useState(null);
  const dateRef = useRef(date);
  dateRef.current = date;

  const load = useCallback(async () => {
    try {
      const r = await api.get("/state", { params: dateRef.current ? { date: dateRef.current } : {} });
      if (tokens.get() && !r.data.me) {
        tokens.clear();
        onLogin();
        return;
      }
      setData(r.data);
      setLastRefresh(new Date().toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta" }));
    } catch (e) {
      /* keep last data */
    }
  }, [onLogin]);

  useEffect(() => { load(); }, [date, load]);
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) load(); }, 8000);
    const vis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", vis); };
  }, [load]);

  const showFab = !!(data?.me?.mustReport && !data?.myToday && !editReport);
  const formKey = editReport ? `edit-${editReport.id}` : `today-${data?.myToday?.code || "new"}`;
  const formVisible = useFormVisible(!!data?.me?.mustReport, `${formKey}-${!!data}`);

  if (!data) {
    return <div className="flex min-h-screen items-center justify-center text-sm font-semibold text-brand" data-testid="dashboard-loading">Memuat dashboard…</div>;
  }

  const order = sectionOrder(data);
  const startEdit = (r) => {
    setEditReport(r);
    setTimeout(() => document.getElementById("form")?.scrollIntoView({ behavior: "smooth" }), 50);
  };
  const render = {
    welcome: () => <WelcomeCard key="welcome" data={data} />,
    form: () => (
      <ReportForm key={formKey} data={data} editReport={editReport}
        onSaved={() => { setEditReport(null); load(); }} onCancelEdit={() => setEditReport(null)} />
    ),
    status: () => <StatusSection key="status" data={data} />,
    blockers: () => <BlockerSection key="blockers" data={data} reload={load} />,
    mine: () => <MyReports key="mine" data={data} onEdit={startEdit} />,
    team: () => <TeamReports key="team" data={data} onEdit={startEdit} reload={load} />,
    discipline: () => <DisciplineGrid key="discipline" data={data} />,
    trends: () => <Trends key="trends" data={data} />,
    ranking: () => <Ranking key="ranking" data={data} />,
    panel: () => <Panel key="panel" data={data} reload={load} />,
  };

  return (
    <div className="mx-auto max-w-6xl px-4 pb-28 sm:px-6 sm:pt-4" data-testid="dashboard">
      <div className="-mx-4 sm:mx-0">
        <Header data={data} date={date} setDate={(d) => { setDate(d); }} lastRefresh={lastRefresh} onLogout={onLogout} onLogin={onLogin} />
      </div>
      <InstallPrompt />
      <SectionNav order={order} />
      <div className="divide-y divide-brand-line/70">{order.map((id) => render[id]())}</div>
      {showFab && !formVisible && (
        <a href="#form" data-testid="fab-fill-report"
          className="fixed bottom-5 left-1/2 z-40 inline-flex min-h-[52px] -translate-x-1/2 items-center gap-2 rounded-full bg-brand px-6 text-sm font-extrabold text-white shadow-[0_14px_30px_-10px_rgba(215,25,31,.8)] md:hidden">
          <PencilLine size={18} /> Isi laporan hari ini
        </a>
      )}
    </div>
  );
}
